<template>
  <footer class="site-footer">

    <!-- Top divider accent -->
    <div class="footer-accent-line" />

    <div class="footer-shell">

      <!-- ── Brand column ────────────────────────────────────────────── -->
      <div class="footer-col brand-col">
        <router-link to="/" class="footer-logo" aria-label="Nexus home">
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
            <rect x="2"  y="2"  width="12" height="12" rx="3" fill="currentColor"/>
            <rect x="18" y="2"  width="12" height="12" rx="3" fill="currentColor" opacity=".4"/>
            <rect x="2"  y="18" width="12" height="12" rx="3" fill="currentColor" opacity=".4"/>
            <rect x="18" y="18" width="12" height="12" rx="3" fill="currentColor"/>
          </svg>
          <span class="footer-wordmark">NEXUS</span>
        </router-link>
        <p class="brand-tagline">Curated goods. Refined taste.<br>Delivered to your door.</p>
        <div class="social-row">
          <a v-for="s in socials" :key="s.label" :href="s.url" class="social-btn" :aria-label="s.label" target="_blank" rel="noopener">
            <component :is="s.icon" />
          </a>
        </div>
      </div>

      <!-- ── Link columns ───────────────────────────────────────────── -->
      <div v-for="col in linkCols" :key="col.title" class="footer-col link-col">
        <h4 class="col-title">{{ col.title }}</h4>
        <ul>
          <li v-for="link in col.links" :key="link.label">
            <router-link v-if="link.internal" :to="link.path">{{ link.label }}</router-link>
            <a v-else :href="link.path" target="_blank" rel="noopener">{{ link.label }}</a>
          </li>
        </ul>
      </div>

      <!-- ── Newsletter column ──────────────────────────────────────── -->
      <div class="footer-col newsletter-col">
        <h4 class="col-title">Stay in the loop</h4>
        <p class="newsletter-desc">New arrivals, exclusive offers, and early access — straight to your inbox.</p>
        <form class="nl-form" @submit.prevent="subscribe">
          <input
            v-model="email"
            type="email"
            placeholder="your@email.com"
            required
            :disabled="nlState !== 'idle'"
          />
          <button type="submit" :disabled="nlState !== 'idle'">
            <span v-if="nlState === 'idle'">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
            </span>
            <span v-else-if="nlState === 'loading'" class="dot-spin" />
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>
          </button>
        </form>
        <p v-if="nlState === 'done'" class="nl-success">Thanks! You're on the list.</p>
      </div>

    </div>

    <!-- ── Bottom bar ─────────────────────────────────────────────────── -->
    <div class="footer-bottom">
      <p class="copyright">© {{ year }} Nexus Commerce, Inc. All rights reserved.</p>
      <div class="legal-links">
        <router-link to="/privacy">Privacy Policy</router-link>
        <span class="sep" />
        <router-link to="/terms">Terms of Service</router-link>
        <span class="sep" />
        <router-link to="/cookies">Cookie Policy</router-link>
      </div>
      <div class="payment-icons" aria-label="Accepted payment methods">
        <span v-for="p in payments" :key="p" class="payment-chip">{{ p }}</span>
      </div>
    </div>

  </footer>
</template>

<script setup>
import { ref, computed, defineComponent, h } from 'vue';

const year = computed(() => new Date().getFullYear());

// Newsletter
const email   = ref('');
const nlState = ref('idle'); // idle | loading | done

async function subscribe() {
  nlState.value = 'loading';
  await new Promise(r => setTimeout(r, 900)); // replace with real API
  nlState.value = 'done';
  email.value = '';
  setTimeout(() => { nlState.value = 'idle'; }, 4000);
}

// Link columns
const linkCols = [
  {
    title: 'Shop',
    links: [
      { label: 'New Arrivals', path: '/new',      internal: true },
      { label: 'Best Sellers', path: '/popular',  internal: true },
      { label: 'Sale',         path: '/sale',      internal: true },
      { label: 'Collections',  path: '/products',  internal: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About',    path: '/about',   internal: true },
      { label: 'Blog',     path: '/blog',    internal: true },
      { label: 'Careers',  path: '/careers', internal: true },
      { label: 'Contact',  path: '/contact', internal: true },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'FAQ',           path: '/faq',      internal: true },
      { label: 'Shipping',      path: '/shipping',  internal: true },
      { label: 'Returns',       path: '/returns',   internal: true },
      { label: 'Track Order',   path: '/track',     internal: true },
    ],
  },
];

// Payment labels
const payments = ['Visa', 'Mastercard', 'AMEX', 'PayPal', 'Apple Pay'];

// Social icons (inline SVG components)
const IconInstagram = defineComponent({ render: () => h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('rect', { x: 2, y: 2, width: 20, height: 20, rx: 5, ry: 5 }),
  h('path', { d: 'M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z' }),
  h('line', { x1: 17.5, y1: 6.5, x2: 17.51, y2: 6.5 }),
]) });

const IconTwitter = defineComponent({ render: () => h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('path', { d: 'M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z' }),
]) });

const IconFacebook = defineComponent({ render: () => h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('path', { d: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z' }),
]) });

const IconYoutube = defineComponent({ render: () => h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('path', { d: 'M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z' }),
  h('polygon', { points: '9.75 15.02 15.5 12 9.75 8.98 9.75 15.02' }),
]) });

const socials = [
  { label: 'Instagram', url: '#', icon: IconInstagram },
  { label: 'Twitter',   url: '#', icon: IconTwitter   },
  { label: 'Facebook',  url: '#', icon: IconFacebook  },
  { label: 'YouTube',   url: '#', icon: IconYoutube   },
];
</script>

<style scoped>
/* ─── Tokens ───────────────────────────────────────────────────────────────── */
.site-footer {
  --bg:        #080808;
  --surface:   #111111;
  --surface-2: #1a1a1a;
  --accent:    #c9a96e;
  --text:      #f2ede5;
  --muted:     rgba(242, 237, 229, 0.38);
  --border:    rgba(255, 255, 255, 0.06);
  --font-disp: 'Cormorant Garamond', Georgia, serif;
  --font-ui:   'DM Sans', 'Segoe UI', sans-serif;

  background: var(--bg);
  font-family: var(--font-ui);
  color: var(--muted);
  position: relative;
}

/* ─── Accent line ─────────────────────────────────────────────────────────── */
.footer-accent-line {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, var(--accent) 35%, rgba(201,169,110,.3) 65%, transparent 100%);
  opacity: .45;
}

/* ─── Main grid ───────────────────────────────────────────────────────────── */
.footer-shell {
  max-width: 1320px;
  margin: 0 auto;
  padding: 60px 28px 48px;
  display: grid;
  grid-template-columns: 1.6fr repeat(3, 1fr) 1.5fr;
  gap: 40px;
}

/* ─── Brand col ───────────────────────────────────────────────────────────── */
.footer-logo {
  display: flex; align-items: center; gap: 9px;
  text-decoration: none;
  color: var(--accent);
  margin-bottom: 16px;
}
.footer-wordmark {
  font-family: var(--font-disp);
  font-size: 1.45rem;
  font-weight: 600;
  letter-spacing: .18em;
  background: linear-gradient(135deg, #f2ede5 20%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.brand-tagline {
  font-size: .78rem;
  line-height: 1.7;
  color: var(--muted);
  margin: 0 0 22px;
}

.social-row { display: flex; gap: 8px; }
.social-btn {
  width: 34px; height: 34px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--muted);
  display: flex; align-items: center; justify-content: center;
  text-decoration: none;
  transition: background .2s, color .2s, border-color .2s, transform .2s;
}
.social-btn:hover {
  background: var(--accent);
  color: #1a1100;
  border-color: var(--accent);
  transform: translateY(-2px);
}

/* ─── Link cols ───────────────────────────────────────────────────────────── */
.col-title {
  font-family: var(--font-ui);
  font-size: .7rem;
  font-weight: 600;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--text);
  margin: 0 0 18px;
}
.link-col ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.link-col a {
  color: var(--muted);
  text-decoration: none;
  font-size: .82rem;
  transition: color .2s, padding-left .2s;
  display: inline-block;
}
.link-col a:hover { color: var(--accent); padding-left: 4px; }

/* ─── Newsletter ──────────────────────────────────────────────────────────── */
.newsletter-desc {
  font-size: .78rem;
  line-height: 1.65;
  margin: 0 0 16px;
}
.nl-form {
  display: flex;
  gap: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color .25s;
}
.nl-form:focus-within { border-color: var(--accent); }
.nl-form input {
  flex: 1;
  background: var(--surface-2);
  border: none;
  color: var(--text);
  font-family: var(--font-ui);
  font-size: .8rem;
  padding: 10px 14px;
  outline: none;
  min-width: 0;
}
.nl-form input::placeholder { color: var(--muted); }
.nl-form input:disabled { opacity: .5; }
.nl-form button {
  width: 42px;
  background: var(--accent);
  border: none;
  color: #1a1100;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: opacity .2s;
  flex-shrink: 0;
}
.nl-form button:hover:not(:disabled) { opacity: .85; }
.nl-form button:disabled { opacity: .6; cursor: not-allowed; }
.nl-success { font-size: .74rem; color: var(--accent); margin: 8px 0 0; }

/* ─── Bottom bar ──────────────────────────────────────────────────────────── */
.footer-bottom {
  max-width: 1320px;
  margin: 0 auto;
  padding: 18px 28px;
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.copyright { font-size: .72rem; color: var(--muted); margin: 0; }

.legal-links { display: flex; align-items: center; gap: 10px; }
.legal-links a {
  font-size: .72rem;
  color: var(--muted);
  text-decoration: none;
  transition: color .2s;
}
.legal-links a:hover { color: var(--accent); }
.sep { width: 3px; height: 3px; background: var(--muted); border-radius: 50%; opacity: .5; }

.payment-icons { display: flex; gap: 6px; flex-wrap: wrap; }
.payment-chip {
  font-size: .62rem;
  font-weight: 600;
  letter-spacing: .04em;
  color: var(--muted);
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 3px 7px;
  text-transform: uppercase;
}

/* ─── Dot spinner ─────────────────────────────────────────────────────────── */
.dot-spin {
  display: inline-block;
  width: 13px; height: 13px;
  border: 2px solid rgba(26,17,0,.3);
  border-top-color: #1a1100;
  border-radius: 50%;
  animation: spin .6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Responsive ──────────────────────────────────────────────────────────── */
@media (max-width: 1100px) {
  .footer-shell {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto auto;
  }
  .brand-col { grid-column: 1 / -1; display: flex; flex-direction: row; align-items: flex-start; gap: 40px; }
  .brand-col .brand-tagline, .brand-col .social-row { margin: 0; }
  .newsletter-col { grid-column: 1 / -1; }
}
@media (max-width: 680px) {
  .footer-shell { grid-template-columns: 1fr 1fr; padding: 40px 20px 32px; gap: 28px; }
  .brand-col { flex-direction: column; gap: 16px; grid-column: 1 / -1; }
  .newsletter-col { grid-column: 1 / -1; }
  .footer-bottom { flex-direction: column; align-items: flex-start; gap: 12px; padding: 16px 20px; }
  .payment-icons { display: none; }
}
@media (max-width: 420px) {
  .footer-shell { grid-template-columns: 1fr; }
}
</style>